/// <amd-module name="Wit/QueriesHub/Editor/LinkedQueryEditor" />

import * as React from "react";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Checkbox } from "VSSUI/Checkbox";
import { Dropdown } from "VSSUI/Dropdown";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { LinkQueryMode } from "Wit/Common/QueryUtils/QueryTypeConstants";
import { ClauseType, IEditInfo, IFilter, IWorkItemLinkType } from "Wit/QueriesHub/Common/Interfaces";
import * as Resources from "Wit/QueriesHub/Resources";
import { IFieldProjectData } from "Wit/WorkItemDataManager/Contracts";
import { EditorTable } from "./EditorTable";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";


export interface ILinkedQueryEditorProps {
    crossProject: boolean;
    editInfo: IEditInfo;
    fieldProjectData: IFieldProjectData;
    fields: IFieldEntry[];
    projectId: string;
    workItemLinkTypes: IWorkItemLinkType[];
    setIsDirty: (isDirty: boolean) => void;
    setFilterState: (filter: IFilter, clauseType: ClauseType) => void;
    setLinkState: (mode?: number, linkTypes?: string, treeLinkTypes?: string, checked?: boolean) => void;
    setStateAddRow: (clauseType: ClauseType, rowIndex?: number) => void;
    setStateRemoveRow: (clauseType: ClauseType, rowIndex: number) => void;
    setClauseFieldState: (field: string, rowIndex: number, clauseType: ClauseType) => void;
    setClauseOperatorState: (operator: string, rowIndex: number, clauseType: ClauseType) => void;
    setClauseValueState: (value: string, rowIndex: number, clauseType: ClauseType) => void;
    setClauseLogicalOperatorState: (logicalOperator: string, rowIndex: number, clauseType: ClauseType) => void;
}

export class LinkedQueryEditor extends VssComponent<ILinkedQueryEditorProps> {
    private dropdownWidth: number = 400;
    private linkQueryDropdownItems: IListBoxItem[] = [];
    private linkQueryDropdownSelection: DropdownSelection;
    private linkTypeCheckboxList: { [linkType: string]: IObservableValue<boolean> } = {};
    private selectedLinkTypeRadioButton: IObservableValue<string>;

    constructor(props: ILinkedQueryEditorProps, context: IVssComponentContext) {
        super(props, context);
        this.createLinkQueryItems();
    }

    public render(): JSX.Element {
        return (
            <>
                <div>
                    <div className="flex-row padding-vertical-16">{Resources.QueriesEditorLinkedText}</div>
                    <EditorTable clauseType={ClauseType.Link} {...this.props} />
                </div>
                <div className={isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.EnableNewBoardsQueriesNewDesign) ? "flex-row padding-top-16 flex-wrap linked-editors" : "flex-row padding-top-16 linked-editors"}>
                    <div className="flex-column rhythm-vertical-4">
                        <span>{Resources.FilterOptions}</span>
                        <Dropdown
                            items={this.linkQueryDropdownItems}
                            selection={this.linkQueryDropdownSelection}
                            width={this.dropdownWidth}
                            onSelect={(event: any, item: IListBoxItem) => {
                                this.props.setLinkState(+item.id);
                                this.props.setIsDirty(true);
                            }}
                        />
                    </div>
                    <div className="flex-column padding-left-16 rhythm-vertical-4">
                        <span>{Resources.TypesOfLinks}</span>
                        <RadioButtonGroup
                            selectedButtonId={this.selectedLinkTypeRadioButton}
                            onSelect={id => {
                                this.selectedLinkTypeRadioButton.value = id;
                                this.props.setIsDirty(true);
                                if (id === "anyLinkType") {
                                    this.props.setLinkState(undefined, "any");
                                }
                            }}
                        >
                            <RadioButton id="anyLinkType" text={Resources.ReturnLinksOfAnyType} />
                            <RadioButton id="selectedLinkType" text={Resources.ReturnSelectedLinkTypes} />
                        </RadioButtonGroup>
                        <Observer returnLinkType={this.selectedLinkTypeRadioButton}>
                            {(props: { returnLinkType: string }) => (
                                <div className="flex-wrap rhythm-vertical-8">
                                    {Object.keys(this.linkTypeCheckboxList).map(linkType => {
                                        const observable = this.linkTypeCheckboxList[linkType];
                                        return (
                                            <Checkbox
                                                checked={observable}
                                                key={linkType}
                                                label={linkType}
                                                onChange={(event: any, checked: boolean) => {
                                                    observable.value = checked;
                                                    this.props.setLinkState(undefined, linkType, undefined, checked);
                                                    this.props.setIsDirty(true);
                                                }}
                                                disabled={props.returnLinkType === "anyLinkType"}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </Observer>
                    </div>
                </div>
            </>
        );
    }

    private createLinkQueryItems = () => {
        if (this.linkQueryDropdownItems.length > 0) {
            return;
        }
        this.linkQueryDropdownItems.push({ id: LinkQueryMode.LinksMustContain.toString(), text: Resources.ReturnItemsThatHaveMatchingLinks });
        this.linkQueryDropdownItems.push({ id: LinkQueryMode.LinksMayContain.toString(), text: Resources.ReturnAllTopLevelItems });
        this.linkQueryDropdownItems.push({ id: LinkQueryMode.LinksDoesNotContain.toString(), text: Resources.ReturnItemsThatDoNotHaveMatchingLinks });
        this.linkQueryDropdownSelection = new DropdownSelection();
        const index = this.linkQueryDropdownItems.findIndex(item => item.id === this.props.editInfo.mode.toString());
        this.linkQueryDropdownSelection.select(index > 0 ? index : 0); // select the 1st option by default
        const linksSet = new Set<string>();
        this.props.workItemLinkTypes
            .filter(link => !link.isRemote)
            .forEach(link => {
                linksSet.add(link.forwardEnd.name);
                linksSet.add(link.reverseEnd.name);
            });
        const linkTypeKeys: string[] = Array.from(linksSet).sort();
        for (let i = 0; i < linkTypeKeys.length; i++) {
            this.linkTypeCheckboxList[linkTypeKeys[i]] = new ObservableValue(false);
        }
        this.selectedLinkTypeRadioButton = new ObservableValue("anyLinkType");
        if (this.props.editInfo.linkTypes && this.props.editInfo.linkTypes !== "") {
            this.selectedLinkTypeRadioButton.value = "selectedLinkType";
            const linkTypesArray = this.props.editInfo.linkTypes.split(",");
            linkTypesArray.forEach(type => {
                this.linkTypeCheckboxList[type].value = true; // set checkboxes to checked if type is in editInfo.linkTypes
            });
        }
    };
}
